import uuid from 'uuid/v4';
import { SeverityLevel } from '@sentry/react';
import {
  CloudFunction,
  AuthenticatedWebsiteRequestBody,
} from '@tomis-tech/types';

import { CLOUD_FUNCTIONS_BASE_URI, IS_DEV, PROJECT_ID } from './consts';
import { addBreadcrumb, logError, Category } from './bot/sentry-iframe';
import { FIREBASE } from './firebase';
import { SITE_SLUG } from './bot/consts';

/** Generate the correct full URL for cloud function */
function generateFullUrl(functionName: CloudFunction, fullUrl: string) {
  /** Should we use cloud.tomis.tech endpoint? */
  const USE_API_FUNCTION_PRODUCTION =
    functionName === CloudFunction.Api && !IS_DEV && PROJECT_ID === 'tomis-bot';

  /** Use cloud.tomis.tech for API function, otherwise cloudfunctions.net domain */
  const cloudFunctionUrl = USE_API_FUNCTION_PRODUCTION
    ? 'https://cloud.tomis.tech'
    : CLOUD_FUNCTIONS_BASE_URI;

  const requestUrl = `${cloudFunctionUrl}${
    USE_API_FUNCTION_PRODUCTION ? '' : functionName
  }/${fullUrl || ''}`;

  return requestUrl;
}

/** Invoke Firebase Function */
export async function invokeFunction<ReturnBodyType>({
  name,
  body: bodyObj,
  failSeverity = 'error',
  fullUrl,
  logToSentry,
}: {
  name: CloudFunction;
  body: object | AuthenticatedWebsiteRequestBody;
  failSeverity?: SeverityLevel;
  /** Use this for the `api` function */
  fullUrl?: string;
  /** If `true`, log any response >= 300 to Sentry.
   *
   * Errors will still be logged to Sentry if fetch fails or
   * there is an error parsing the response body.
   */
  logToSentry?: boolean;
}): Promise<[ReturnBodyType, Response]> {
  const invocationId = uuid();
  const newBodyObj: object = {
    invocationId,
    ...bodyObj,
  };

  const firebaseUser = FIREBASE.auth.currentUser;

  const url = generateFullUrl(name, fullUrl);

  const headers = new Headers();
  headers.set('Accept', 'application/json');
  headers.set('Content-Type', 'application/json');
  headers.set('x-site-slug', SITE_SLUG);

  if (firebaseUser) {
    const firebaseToken = await firebaseUser.getIdToken();
    headers.set('x-firebase-token', firebaseToken);
  }

  const requestBody = JSON.stringify(newBodyObj);
  const category = Category[name];

  const requestData = {
    invocationId,
    requestUrl: url,
    requestBody,
    uid: firebaseUser ? firebaseUser.uid : null,
  };

  addBreadcrumb({
    category,
    message: `invoking ${name}`,
    level: 'info',
    data: requestData,
  });

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      // Allow different origin for API function
      mode: 'cors',
      body: requestBody,
    });
  } catch (err) {
    logError(err, {
      category,
      message: `Error requesting ${name}`,
      level: failSeverity,
      type: 'error',
      data: requestData,
    });
    throw err;
  }

  let responseBody: ReturnBodyType;
  try {
    responseBody = await response.json();
  } catch (err) {
    logError(err, {
      category,
      message: `Error parsing ${name} response's body`,
      level: failSeverity,
      type: 'error',
      data: requestData,
    });
    throw err;
  }

  if (response.ok) {
    addBreadcrumb({
      category,
      message: `${name} response ok`,
      level: 'info',
      data: {
        invocationId,
        responseBody,
        uid: firebaseUser ? firebaseUser.uid : null,
      },
    });
  } else if (logToSentry) {
    logError(new Error(`${name} response not ok`), {
      category,
      message: `${name} response not ok`,
      level: failSeverity,
      type: 'error',
      data: {
        invocationId,
        responseBody,
        uid: firebaseUser ? firebaseUser.uid : null,
      },
    });
  }

  return [responseBody, response];
}
